import { useCallback, useMemo, useRef, useState } from 'react'
import { useTemplates, type Template } from '../hooks/useTemplates'
import { useTemplateRevisions, type TemplateRevision } from '../hooks/useTemplateRevisions'
import { usePersonalTeam, useSharedTeam } from '../hooks/useTeam'
import { useToast } from '../hooks/useToast'

const VARIABLES = ['{{first_name}}', '{{last_name}}', '{{full_name}}', '{{company}}', '{{title}}']

/**
 * Templates (snippets) page — Kondo-style two-section layout.
 *
 *   - Personal snippets   — `template.team_id === personalTeam.id`
 *   - Team snippets       — `template.team_id === sharedTeam.id`
 *
 * No switcher: both sections are always visible. If the user isn't in a
 * shared team, the Team section renders a placeholder prompting them to
 * create / accept an invite.
 */
export default function Templates() {
  const {
    templates,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    moveTemplateToTeam,
    copyTemplateToPersonal,
  } = useTemplates()
  const personalTeam = usePersonalTeam()
  const sharedTeam = useSharedTeam()
  // Surface hook mutation failures (PR 2 of code-review handoff): the
  // hook now throws on Supabase error, so every handler that awaits one
  // needs an error path. Toasts are the lowest-friction surface that
  // matches the rest of the app (ThreadView).
  const { toast, show: showToast } = useToast()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [editBody, setEditBody] = useState('')
  const [filterText, setFilterText] = useState('')
  // History panel (kondo-parity 1.13). Toggled per-template so
  // switching snippets doesn't leave a dangling panel open against
  // the wrong history.
  const [historyOpen, setHistoryOpen] = useState(false)
  const { revisions, loading: revisionsLoading } = useTemplateRevisions(historyOpen ? selectedId : null)

  const { personalTemplates, teamTemplates } = useMemo(() => {
    const match = (t: Template) =>
      !filterText ||
      t.name.toLowerCase().includes(filterText.toLowerCase()) ||
      t.body.toLowerCase().includes(filterText.toLowerCase())
    return {
      personalTemplates: templates.filter((t) => t.team_id === personalTeam?.id && match(t)),
      teamTemplates: templates.filter((t) => t.team_id === sharedTeam?.id && match(t)),
    }
  }, [templates, personalTeam?.id, sharedTeam?.id, filterText])

  // Memoised so unrelated `templates`-array churn (realtime tick that changes
  // no field the selected row cares about) doesn't trigger a fresh `.find`
  // walk on every render. Lists are small today but this will matter more
  // as teams grow — O(n) per render vs. O(n) per selection change.
  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) ?? null,
    [templates, selectedId],
  )

  // Track the textarea so "Insert variable" can splice at the caret position
  // instead of naive append-to-end. Caret position is read at click time;
  // the textarea focus is restored afterwards so the user keeps typing in
  // the same spot they were.
  const bodyRef = useRef<HTMLTextAreaElement | null>(null)

  const insertVariable = useCallback((variable: string) => {
    const el = bodyRef.current
    if (!el) {
      // Fallback — append if we don't have the textarea handle yet. Matches
      // the pre-polish behaviour exactly.
      setEditBody((prev) => prev + variable)
      return
    }
    const start = el.selectionStart ?? el.value.length
    const end = el.selectionEnd ?? el.value.length
    const next = el.value.slice(0, start) + variable + el.value.slice(end)
    setEditBody(next)
    // Restore focus + caret immediately after the next render tick so the
    // cursor lands just past the inserted variable. requestAnimationFrame
    // is the right moment: the textarea's value has already been patched
    // by React's render, but the browser hasn't re-flowed.
    requestAnimationFrame(() => {
      const target = bodyRef.current
      if (!target) return
      target.focus()
      const caret = start + variable.length
      target.setSelectionRange(caret, caret)
    })
  }, [])

  const selectTemplate = (t: Template) => {
    setSelectedId(t.id)
    setEditName(t.name)
    setEditBody(t.body)
  }

  const handleNew = async (teamId: string) => {
    try {
      const tpl = await addTemplate('new-template', 'Hey {{first_name}}, ', teamId)
      selectTemplate(tpl)
    } catch (e) {
      showToast(`Failed to create snippet: ${e instanceof Error ? e.message : 'unknown error'}`)
    }
  }

  // Track in-flight Save / Delete so we can disable the buttons while the
  // mutation is pending. useMutation's own isPending isn't exposed through
  // the useTemplates wrapper today — a lightweight local flag keeps the
  // scope of this polish small without threading flags through the hook.
  const [savePending, setSavePending] = useState(false)
  const [deletePending, setDeletePending] = useState(false)

  const handleSave = async () => {
    if (!selectedId || savePending) return
    setSavePending(true)
    try {
      await updateTemplate(selectedId, { name: editName, body: editBody })
    } catch (e) {
      // Don't clear the editor buffer — the user's text is still the
      // freshest state; let them retry.
      showToast(`Save failed: ${e instanceof Error ? e.message : 'unknown error'}`)
    } finally {
      setSavePending(false)
    }
  }

  const handleDelete = async () => {
    if (!selectedId || deletePending) return
    setDeletePending(true)
    try {
      await deleteTemplate(selectedId)
      // Only clear selection after the server confirms. Previously we
      // always cleared, hiding delete failures behind an apparently-
      // successful UI transition.
      setSelectedId(null)
    } catch (e) {
      showToast(`Delete failed: ${e instanceof Error ? e.message : 'unknown error'}`)
    } finally {
      setDeletePending(false)
    }
  }

  // Dirty = there are unsaved edits. Used to disable the Save button so
  // idle clicks don't write no-op updates that still invalidate the cache.
  const isDirty =
    selected != null &&
    (editName !== selected.name || editBody !== selected.body)

  /**
   * Share the currently-selected personal snippet with the shared team.
   * Saves any in-flight edits first so the team sees the latest copy, not
   * a stale version. After the move, the row still exists (same id) — we
   * keep it selected so the user sees the Team badge flip on.
   */
  const handleShareWithTeam = async () => {
    if (!selected || !sharedTeam) return
    try {
      // Flush pending edits so we don't ship stale content to teammates.
      // If the flush fails, BAIL before moving — otherwise the team
      // gets a pre-edit version and the user thinks they shared their
      // edited copy.
      if (editName !== selected.name || editBody !== selected.body) {
        await updateTemplate(selected.id, { name: editName, body: editBody })
      }
      await moveTemplateToTeam(selected.id, sharedTeam.id)
    } catch (e) {
      showToast(`Share failed: ${e instanceof Error ? e.message : 'unknown error'}`)
    }
  }

  /**
   * Fork a team snippet into a personal copy. Swaps selection to the new
   * row so the user can immediately edit "their" version without worrying
   * about clobbering the team original.
   */
  const handleCopyToPersonal = async () => {
    if (!selected || !personalTeam) return
    try {
      const tpl = await copyTemplateToPersonal(selected, personalTeam.id)
      selectTemplate(tpl)
    } catch (e) {
      showToast(`Copy failed: ${e instanceof Error ? e.message : 'unknown error'}`)
    }
  }

  return (
    <div className="relative flex flex-1 overflow-hidden">
      {/* Template list */}
      <div className="flex w-[340px] flex-col border-r border-zinc-200 bg-white">
        <div className="border-b border-zinc-100 px-4 py-[18px]">
          <span className="text-sm font-semibold text-zinc-900">Snippets</span>
        </div>

        <div className="px-4 py-2.5">
          <input
            type="text"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            placeholder="Filter snippets..."
            className="w-full rounded-lg bg-zinc-100 px-3 py-[7px] text-xs text-zinc-900 placeholder:text-zinc-400 focus:outline-none"
          />
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Personal section */}
          <SectionHeader
            label="Personal"
            onAdd={personalTeam ? () => handleNew(personalTeam.id) : undefined}
          />
          {personalTemplates.length === 0 ? (
            <div className="px-4 py-2 text-[11px] text-zinc-400">
              {templates.some((t) => t.team_id === personalTeam?.id)
                ? 'No matches'
                : 'No personal snippets yet'}
            </div>
          ) : (
            personalTemplates.map((t) => (
              <TemplateRow
                key={t.id}
                t={t}
                isSelected={selectedId === t.id}
                onSelect={() => selectTemplate(t)}
              />
            ))
          )}

          {/* Team section */}
          <SectionHeader
            label={sharedTeam ? `${sharedTeam.name} — Team` : 'Team snippets'}
            onAdd={sharedTeam ? () => handleNew(sharedTeam.id) : undefined}
          />
          {!sharedTeam ? (
            <div className="px-4 py-3 text-[11px] text-zinc-400">
              You're not in a team yet.{' '}
              <span className="text-zinc-500">Create one in Settings → Team.</span>
            </div>
          ) : teamTemplates.length === 0 ? (
            <div className="px-4 py-2 text-[11px] text-zinc-400">
              {templates.some((t) => t.team_id === sharedTeam.id)
                ? 'No matches'
                : 'No team snippets yet'}
            </div>
          ) : (
            teamTemplates.map((t) => (
              <TemplateRow
                key={t.id}
                t={t}
                isSelected={selectedId === t.id}
                onSelect={() => selectTemplate(t)}
              />
            ))
          )}
        </div>
      </div>

      {/* Editor */}
      <div className="flex flex-1 flex-col bg-white">
        {selected ? (
          <>
            <div className="flex items-center justify-between border-b border-zinc-100 px-8 py-[18px]">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-zinc-900">Edit snippet</span>
                {selected.team_id === sharedTeam?.id && (
                  <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                    Team
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {/* Scope-move action: visible only when the move is actually
                    possible. Personal→Team needs a shared team to exist;
                    Team→Personal just needs the personal team (which
                    always does, post-signup). */}
                {selected.team_id === personalTeam?.id && sharedTeam && (
                  <button
                    onClick={handleShareWithTeam}
                    className="rounded-[7px] border border-zinc-200 px-3.5 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
                    title={`Move this snippet into ${sharedTeam.name} so teammates can use it`}
                  >
                    Share with team
                  </button>
                )}
                {selected.team_id === sharedTeam?.id && personalTeam && (
                  <button
                    onClick={handleCopyToPersonal}
                    className="rounded-[7px] border border-zinc-200 px-3.5 py-1.5 text-xs font-medium text-zinc-600 hover:bg-zinc-50"
                    title="Create a personal copy you can edit without affecting the team"
                  >
                    Save to Personal
                  </button>
                )}
                <button
                  onClick={handleDelete}
                  disabled={deletePending}
                  className="rounded-[7px] border border-zinc-200 px-3.5 py-1.5 text-xs font-medium text-zinc-500 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {deletePending ? 'Deleting…' : 'Delete'}
                </button>
                {/* History (ADR 0034) — opens a revision panel in the
                 * right rail. Button is disabled when the template is
                 * unsaved (no id yet) or we're mid-save, to keep the
                 * panel in sync with committed state. */}
                <button
                  onClick={() => setHistoryOpen((v) => !v)}
                  disabled={savePending || !selected}
                  className={`rounded-[7px] border px-3.5 py-1.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-60 ${
                    historyOpen
                      ? 'border-blue-300 bg-blue-50 text-blue-700'
                      : 'border-zinc-200 text-zinc-600 hover:bg-zinc-50'
                  }`}
                  title="Show edit history"
                  aria-pressed={historyOpen}
                >
                  History
                </button>
                <button
                  onClick={handleSave}
                  disabled={savePending || !isDirty}
                  className="rounded-[7px] bg-zinc-900 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {savePending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>

            <div className="flex flex-1 flex-col gap-6 p-8">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-medium text-zinc-600">Trigger</label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value.replace(/\s/g, '-').toLowerCase())}
                  className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-[9px] text-[13px] text-zinc-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <span className="text-[11px] text-zinc-400">
                  Type <span className="font-medium">/{editName}</span> in the compose box to insert
                </span>
              </div>

              <div className="flex flex-1 flex-col gap-1.5">
                <label className="text-xs font-medium text-zinc-600">Message body</label>
                <textarea
                  ref={bodyRef}
                  value={editBody}
                  onChange={(e) => setEditBody(e.target.value)}
                  className="flex-1 resize-none rounded-lg border border-zinc-200 bg-zinc-50 p-3 text-[13px] leading-relaxed text-zinc-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="text-xs font-medium text-zinc-600">Available variables</label>
                <div className="flex flex-wrap gap-1.5">
                  {VARIABLES.map((v) => (
                    <button
                      key={v}
                      onClick={() => insertVariable(v)}
                      className="rounded-md bg-zinc-100 px-2.5 py-[3px] text-[11px] font-medium text-zinc-600 hover:bg-zinc-200"
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {historyOpen && selected && (
              <HistoryPanel
                loading={revisionsLoading}
                revisions={revisions}
                currentName={editName}
                currentBody={editBody}
                onRestore={(rev) => {
                  // Restore = populate the editor with the revision's
                  // values; leave the actual UPDATE to the user (they
                  // click Save). Preserves the "nothing commits until
                  // you hit Save" invariant of the rest of the page.
                  setEditName(rev.name)
                  setEditBody(rev.body)
                  setHistoryOpen(false)
                }}
                onClose={() => setHistoryOpen(false)}
              />
            )}
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <p className="text-3xl text-zinc-200">/</p>
              <p className="mt-2 text-sm text-zinc-400">Select a snippet to edit</p>
            </div>
          </div>
        )}
      </div>

      {/* Error toast — fires on mutation failures so silent failures are
          impossible. Auto-dismisses per useToast default (3s). */}
      {toast && (
        <div
          role="status"
          className="pointer-events-none absolute bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-lg bg-zinc-900 px-4 py-2 text-xs text-white shadow-lg"
        >
          {toast}
        </div>
      )}
    </div>
  )
}

// --- History panel (ADR 0034 / kondo-parity 1.13) --------------------------

function HistoryPanel({
  loading,
  revisions,
  currentName,
  currentBody,
  onRestore,
  onClose,
}: {
  loading: boolean
  revisions: TemplateRevision[]
  currentName: string
  currentBody: string
  onRestore: (rev: TemplateRevision) => void
  onClose: () => void
}) {
  return (
    <div className="border-t border-zinc-200 bg-zinc-50 px-8 py-6">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-[10px] font-medium tracking-wider text-zinc-400">EDIT HISTORY</span>
        <button onClick={onClose} className="text-xs text-zinc-400 hover:text-zinc-600">Close</button>
      </div>
      {loading ? (
        <p className="text-xs text-zinc-400">Loading…</p>
      ) : revisions.length === 0 ? (
        <p className="text-xs text-zinc-400">No prior edits yet — this snippet hasn't been changed since it was created.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {revisions.map((rev) => {
            // A revision matching the current buffer would be a no-op
            // restore — dim the button to signal "nothing to do here"
            // without removing the entry (users may want to confirm
            // that's what the current state matches).
            const matchesCurrent = rev.name === currentName && rev.body === currentBody
            return (
              <li key={rev.id} className="flex items-start gap-3 rounded-lg border border-zinc-200 bg-white p-3">
                <div className="flex-1">
                  <p className="text-xs font-medium text-zinc-700">{rev.name}</p>
                  <p className="mt-1 line-clamp-2 text-[11px] text-zinc-500">{rev.body}</p>
                  <p className="mt-1.5 text-[10px] text-zinc-400">
                    {new Date(rev.created_at).toLocaleString()}
                  </p>
                </div>
                <button
                  onClick={() => onRestore(rev)}
                  disabled={matchesCurrent}
                  className="shrink-0 rounded-md border border-zinc-200 px-2.5 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
                  title={matchesCurrent ? 'This is the current version' : 'Copy into the editor'}
                >
                  Restore
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// --- Small internal bits ---------------------------------------------------

function SectionHeader({ label, onAdd }: { label: string; onAdd?: () => void }) {
  return (
    <div className="flex items-center justify-between px-4 pb-1 pt-3">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
        {label}
      </span>
      {onAdd && (
        <button
          onClick={onAdd}
          className="flex h-5 w-5 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
          title={`New ${label.toLowerCase()} snippet`}
        >
          <span className="text-sm leading-none">+</span>
        </button>
      )}
    </div>
  )
}

function TemplateRow({
  t,
  isSelected,
  onSelect,
}: {
  t: Template
  isSelected: boolean
  onSelect: () => void
}) {
  return (
    <button
      onClick={onSelect}
      className={`flex w-full flex-col gap-[3px] px-4 py-2.5 text-left ${
        isSelected
          ? 'border-l-2 border-blue-500 bg-blue-50/60'
          : 'border-b border-zinc-50 hover:bg-zinc-50'
      }`}
    >
      <span className="text-[13px] font-semibold text-zinc-900">/{t.name}</span>
      <span className="truncate text-xs text-zinc-400">{t.body}</span>
    </button>
  )
}
